import { motion } from "framer-motion";
import { Reveal, EASE } from "./primitives";
import { useLanguage } from "../../i18n/LanguageContext";

const GAMEDAY_PHOTOS = [
  "/images/gameday-officials.jpg",
  "/images/gameday-broadcast.jpg",
  "/images/gameday-medical.jpg",
];

export const GameDay = () => {
  const { t } = useLanguage();
  const G = t.gameday;
  return (
    <section className="noise relative overflow-hidden bg-kn-navy py-24 lg:py-36" data-testid="gameday-section">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(1000px 600px at 50% 50%, rgba(15,116,196,0.22), transparent 65%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal className="text-center">
          <p className="font-mono text-[11px] tracking-[0.32em] uppercase text-kn-cyan">{G.eyebrow}</p>
          <h2 className="mx-auto mt-6 max-w-4xl text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl">
            {G.h2a}{" "}
            <span className="font-serif italic font-normal text-brand-gradient block">{G.h2b}</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/60">{G.body}</p>
        </Reveal>

        <Reveal delay={0.15} className="mt-16">
          <div
            className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-4 [grid-auto-flow:dense]"
            data-testid="gameday-grid"
          >
            {/* centered hub on desktop */}
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: EASE }}
              className="col-span-2 flex flex-col items-center justify-center bg-kn-navy2 px-6 py-10 sm:col-start-2 sm:row-start-3"
              data-testid="gameday-hub"
            >
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-kn-cyan">Every fixture needs</span>
              <span className="mt-2 font-serif text-4xl italic text-white sm:text-5xl">{G.hub}</span>
            </motion.div>
            {G.roles.map((r, i) => (
              <motion.div
                key={r}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.03, ease: EASE }}
                className="flex items-center justify-center bg-kn-navy px-4 py-6 text-center"
              >
                <span className="text-[13px] font-medium tracking-wide text-white/70">{r}</span>
              </motion.div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.2} className="mt-6">
          <div className="grid gap-3 sm:grid-cols-3" data-testid="gameday-photos">
            {GAMEDAY_PHOTOS.map((src, i) => (
              <div key={src} className="group relative aspect-[4/3] overflow-hidden rounded-xl" data-testid={`gameday-photo-${i}`}>
                <img src={src} alt={G.photos[i]} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-kn-navy/85 via-kn-navy/20 to-transparent" />
                <p className="absolute bottom-4 left-5 font-mono text-[10px] tracking-[0.24em] uppercase text-white/85">
                  {G.photos[i]}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
